/// <reference lib="dom" />
/// <reference lib="dom.iterable" />
import { assert } from "./_devmode.js";

type PubRecord = {
  citation: HTMLElement;
  year?: string;
  date?: string;
  position: number;
  count?: number;
  gcount?: number;
};

type SortKey = string;
type SourceKey = string;
type DisplayState = {
  order: SortKey;
  source: SourceKey;
};

function groupBy<T>(arr: T[], key: (x: T) => string): Record<string, T[]> {
  let result: Record<string, T[]> = {};
  for (let x of arr) {
    let k = key(x);
    if (!result[k]) result[k] = [];
    result[k].push(x);
  }
  return result;
}

function maybeParseInt(text: string | undefined | null): number | undefined {
  if (text != undefined) {
    return Number.parseInt(text);
  } else {
    return undefined;
  }
}

let pubs: PubRecord[];
let pubRoot: HTMLElement;

function setSortKey(key: SortKey) {
  console.log("sorting by %s", key);
  pubRoot.dataset.sortKey = key;
  for (let ee of pubRoot.querySelectorAll(".pub-sort-method .entry")) {
    assert(ee instanceof HTMLElement);
    ee.classList.remove("active");
    if (ee.dataset.order == key) {
      ee.classList.add("active");
    }
  }
}

function setCiteSource(key: SourceKey) {
  let main = document.getElementById("publications");
  console.log("using source %s", key);
  main!.dataset.citeSource = key;
  for (let ee of pubRoot.querySelectorAll(".pub-cite-source .entry")) {
    assert(ee instanceof HTMLElement);
    ee.classList.remove("active");
    if (ee.dataset.source == key) {
      ee.classList.add("active");
    }
  }
}

function sortedByDate() {
  let sorted = pubs.toSorted((c1, c2) => {
    let d1 = c1.date ?? "";
    let d2 = c2.date ?? "";
    if (d1 > d2) {
      return -1;
    } else if (d1 < d2) {
      return 1;
    } else {
      return c1.position - c2.position;
    }
  });
  let groups = groupBy(sorted, (c) => c.year ?? "UNK");
  let years = Object.keys(groups);
  years.sort();
  years.reverse();
  console.log("years:", years);
  let elt = (
    <div class="ps-shell by-date">
      {years.map((y) => {
        let cites = groups[y];
        return (
          <div>
            <h2>
              {y} <span class="cat-count">// {cites.length}</span>
            </h2>
            <div class="citelist">
              {cites.map((e) => e.citation.cloneNode(true))}
            </div>
          </div>
        );
      })}
    </div>
  );
  console.log("recent view ready");
  return elt;
}

function sortedByCitations(src?: SourceKey) {
  let sorted = pubs.slice();
  sorted.sort(function(c1, c2) {
    let n1, n2;
    switch (src) {
      case "semantic":
        n1 = c1.count ?? 0;
        n2 = c2.count ?? 0;
        break;
      case "google":
        n1 = c1.gcount ?? 0;
        n2 = c2.gcount ?? 0;
        break;
      default:
        console.warn("invalid citation source %s", src);
        n1 = n2 = 0;
    }
    return n2 - n1;
  });
  let elt = (
    <ol class={`citelist ps-shell by-cited ${src}`}>
      {sorted.map(function(e, i) {
        let count = src == "google" ? e.gcount : e.count;
        return (
          <li class={(count ?? 0) >= i + 1 ? "hcounted" : null}>
            {e.citation.cloneNode(true)}
          </li>
        );
      })},
    </ol>
  );
  console.debug("cited view ready");
  return elt;
}

function makeSortMenu() {
  let menu = (
    <div class="toggle-menu pub-sort-method">
      <div class="label">{"display mode: "}</div>
      <div class="entry" data-order="type">by type</div>
      <div class="entry" data-order="date">most recent</div>
      <div class="entry" data-order="cited">most cited</div>
    </div>
  );
  for (let x of menu.querySelectorAll("div.entry")) {
    x.addEventListener("click", navigateSort);
  }
  return menu;
}

function makeSourceMenu() {
  let menu = (
    <div class="toggle-menu pub-cite-source">
      <div class="label">{"cite count source: "}</div>
      <div class="entry" data-source="google">Google</div>
      <div class="entry" data-source="semantic">Semantic Scholar</div>
    </div>
  );
  for (let x of menu.querySelectorAll("div.entry")) {
    x.addEventListener("click", navigateSort);
  }
  return menu;
}

function scanPublicationList(): PubRecord[] {
  let pubs = [];
  let cites = document.querySelectorAll(".citation, .cite-container");
  for (let i = 0; i < cites.length; i++) {
    let e = cites[i];
    assert(e instanceof HTMLElement);
    if (e.matches(".cite-container .citation")) {
      console.log("found citation in container, skipping");
      continue;
    }
    let ce = e.matches(".cite-container") ? e.querySelector(".citation") : e;
    if (!(ce instanceof HTMLElement)) {
      console.error("cannot find citation for %s", e);
      continue;
    }
    let yr = ce.dataset.year;
    let cc = maybeParseInt(ce.dataset.semanticCitations);
    let gc = maybeParseInt(ce.dataset.googleCitations);
    let record: PubRecord = {
      citation: e,
      year: yr,
      date: ce.dataset.date,
      position: i,
      count: cc,
      gcount: gc,
    };
    console.log("record", record);
    pubs.push(record);
  }
  console.log("found %d citations", pubs.length);
  return pubs;
}

function parseUrlState(): DisplayState {
  let url = new URL(window.location.href);
  console.debug("parsing from URL %o", url);
  return {
    order: url.searchParams?.get("order") ?? "type",
    source: url.searchParams?.get("source") ?? "google",
  };
}

function makeStateURL(state: DisplayState) {
  return `?order=${state.order}&source=${state.source}`;
}

function getSortState(evt?: PopStateEvent): DisplayState {
  let state = evt?.state;
  state ??= window.history.state;
  state ??= parseUrlState();
  return state;
}

function navigateSort(evt: Event) {
  let state = getSortState();
  let element = evt.target;
  assert(element == null || element instanceof HTMLElement);
  if (element?.dataset.order) {
    state.order = element.dataset.order;
    setTimeout(() => setSortKey(state.order), 0);
  }
  if (element?.dataset.source) {
    state.source = element.dataset.source;
    setTimeout(() => setCiteSource(state.source), 0);
  }
  let url = makeStateURL(state);
  console.debug("navigating to %s: %o", url, state);
  window.history.pushState(state, "", url);
}

function updateSortDisplay(evt?: PopStateEvent) {
  let state = getSortState(evt);
  console.debug("updating to state %o", state);
  setSortKey(state.order);
  setCiteSource(state.source);
}

function setup() {
  pubs = scanPublicationList();
  pubRoot = document.querySelector(".pub-sorter")!;
  // pubRoot.prepend(makeSourceMenu());
  pubRoot.prepend(makeSortMenu());
  pubRoot.append(sortedByDate());
  pubRoot.append(sortedByCitations("semantic"));
  pubRoot.append(sortedByCitations("google"));

  let main = document.getElementById("publications");
  main?.classList.add("cite-options");

  addEventListener("popstate", updateSortDisplay);
  updateSortDisplay();
}

setup();
